import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import { it } from 'date-fns/locale';
import { Month } from 'date-fns';

type DataPoint = { x: number; y: number };

type GraphSwitchProps = {
	isVisible: boolean;
	label: string;
	icon: React.ReactElement;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const circle = d3.symbol().type(d3.symbolCircle).size(80);
const triangle = d3.symbol().type(d3.symbolTriangle).size(80);
const square = d3.symbol().type(d3.symbolSquare).size(80);
const cross = d3.symbol().type(d3.symbolCross).size(80);

const ButtonSwitch = ({ isVisible, label, icon, ...props }: GraphSwitchProps) => {
	return (
		<button
			{...props}
			style={{
				display: 'flex',
				gap: '8px',
				alignItems: 'center',
				borderColor: isVisible ? '#4400b3' : '#ABB7C3',
				borderStyle: 'solid',
				borderWidth: '3px',
				borderRadius: '10px',
				padding: '6px 10px',
				backgroundColor: 'white',
			}}
		>
			<svg width="20" height="20">
				{icon}
			</svg>
			<span style={{ color: isVisible ? 'black' : '#ABB7C3', fontSize: '18px' }}>{label}</span>
		</button>
	);
};

type LineChartProps = {
	data: {
		amount: DataPoint[];
		finalAmount: DataPoint[];
		cashedAmount: DataPoint[];
		cashedInAmount: DataPoint[];
	};
	onDataPointClick: (point: number) => void;
	isFocused?: boolean;
};

const lineColors = {
	1: '#0000a1',
	2: '#6161ff',
	3: '#9696ff',
	4: '#c300c3',
};

const LineChart = ({ data, isFocused, onDataPointClick }: LineChartProps) => {
	const [line1Visible, setLine1Visible] = useState(true);
	const [line2Visible, setLine2Visible] = useState(true);
	const [line3Visible, setLine3Visible] = useState(true);
	const [line4Visible, setLine4Visible] = useState(true);

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		setLine1Visible(true);
		setLine2Visible(true);
		setLine3Visible(true);
		setLine4Visible(true);

		const svg = d3.select(svgRef.current);
		svg.selectAll('*').remove();

		const width = 900;
		const height = 300;
		const margin = 50;

		const allXValues = [...new Set([...data.amount.map((d) => d.x), ...data.finalAmount.map((d) => d.x)])].sort((a, b) => a - b);

		const allYValues = [
			...data.amount.map((d) => d.y),
			...data.finalAmount.map((d) => d.y),
			...data.cashedAmount.map((d) => d.y),
			...data.cashedInAmount.map((d) => d.y),
		];

		const yMin = Math.min(...allYValues);
		const yMax = Math.max(...allYValues);
		const xMax = Math.max(data.amount.length, data.finalAmount.length, data.cashedAmount.length, data.cashedInAmount.length);

		const xScale = d3
			.scalePoint<number>()
			.domain(allXValues)
			.range([margin + 20, width - margin]);

		const yScale = d3
			.scaleLinear()
			.domain([yMin, yMax])
			.range([height - margin, margin]);

		const lineGenerator = d3
			.line<DataPoint>()
			.x((d) => xScale(d.x))
			.y((d) => yScale(d.y));

		svg
			.append('path')
			.attr('class', 'line1')
			.datum(data.amount)
			.attr('fill', 'none')
			.attr('stroke', lineColors[1])
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.append('path')
			.attr('class', 'line2')
			.datum(data.finalAmount)
			.attr('fill', 'none')
			.attr('stroke', lineColors[2])
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.append('path')
			.attr('class', 'line3')
			.datum(data.cashedAmount)
			.attr('fill', 'none')
			.attr('stroke', lineColors[3])
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.append('path')
			.attr('class', 'line4')
			.datum(data.cashedInAmount)
			.attr('fill', 'none')
			.attr('stroke', lineColors[4])
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.selectAll('.dot1')
			.data(data.amount)
			.enter()
			.append('path')
			.attr('class', 'dot1')
			.attr('d', circle)
			.attr('fill', lineColors[1])
			.attr('transform', (d) => `translate(${xScale(d.x)},${yScale(d.y)})`);

		svg
			.selectAll('.dot2')
			.data(data.finalAmount)
			.enter()
			.append('path')
			.attr('class', 'dot2')
			.attr('d', triangle)
			.attr('fill', lineColors[2])
			.attr('transform', (d) => `translate(${xScale(d.x)},${yScale(d.y)})`);

		svg
			.selectAll('.dot3')
			.data(data.cashedAmount)
			.enter()
			.append('path')
			.attr('class', 'dot3')
			.attr('d', square)
			.attr('fill', lineColors[3])
			.attr('transform', (d) => `translate(${xScale(d.x)},${yScale(d.y)})`);

		svg
			.selectAll('.dot4')
			.data(data.cashedInAmount)
			.enter()
			.append('path')
			.attr('class', 'dot4')
			.attr('d', cross)
			.attr('fill', lineColors[4])
			.attr('transform', (d) => `translate(${xScale(d.x)},${yScale(d.y)})`);

		const xAxis = d3
			.axisBottom(xScale)
			.ticks(xMax)
			.tickFormat((d) => it.localize.month((d - 1) as Month, { width: 'abbreviated' }) ?? String(d));
		const yAxis = d3.axisLeft(yScale);

		const xAxisGroup = svg
			.append('g')
			.attr('transform', `translate(0,${height - margin})`)
			.call(xAxis);

		if (!isFocused) {
			const ticks = xAxisGroup.selectAll<SVGGElement, number>('.tick');

			ticks
				.insert('rect', 'text')
				.attr('class', 'tick-hitbox')
				.attr('x', -30)
				.attr('y', -20)
				.attr('width', 100)
				.attr('height', 60)
				.attr('fill', 'transparent');
		}

		svg.append('g').attr('transform', `translate(${margin},0)`).call(yAxis);
	}, [data, onDataPointClick, isFocused]);

	return (
		<div style={{ position: 'relative' }}>
			<svg ref={svgRef} width={900} height={300} />
			<div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', position: 'absolute', top: -96, right: 0 }}>
				<ButtonSwitch
					isVisible={line1Visible}
					label="Spesa prevista"
					icon={<circle cx="10" cy="10" r="7" fill={lineColors[1]} />}
					onClick={() => {
						const display = line1Visible ? 'none' : null;
						d3.selectAll('.line1').style('display', display);
						d3.selectAll('.dot1').style('display', display);
						setLine1Visible((isVisible) => !isVisible);
					}}
				/>
				<ButtonSwitch
					isVisible={line2Visible}
					label="Spesa effettuata"
					icon={<path d={triangle()} fill={lineColors[2]} transform="translate(10,10)" />}
					onClick={() => {
						const display = line2Visible ? 'none' : null;
						d3.selectAll('.line2').style('display', display);
						d3.selectAll('.dot2').style('display', display);
						setLine2Visible((isVisible) => !isVisible);
					}}
				/>
				<ButtonSwitch
					isVisible={line3Visible}
					label="Incasso previsto"
					icon={<path d={square()} fill={lineColors[3]} transform="translate(10,10)" />}
					onClick={() => {
						const display = line3Visible ? 'none' : null;
						d3.selectAll('.line3').style('display', display);
						d3.selectAll('.dot3').style('display', display);
						setLine3Visible((isVisible) => !isVisible);
					}}
				/>
				<ButtonSwitch
					isVisible={line4Visible}
					label="Incasso effettivo"
					icon={<path d={cross()} transform="translate(10,10)" fill={lineColors[4]} />}
					onClick={() => {
						const display = line4Visible ? 'none' : null;
						d3.selectAll('.line4').style('display', display);
						d3.selectAll('.dot4').style('display', display);
						setLine4Visible((isVisible) => !isVisible);
					}}
				/>
			</div>
		</div>
	);
};

export default LineChart;
